import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Clock, Search, Star, SlidersHorizontal, X, TrendingUp } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';
import { useCurrency } from '../contexts/CurrencyContext';
import { useProductTranslation } from '../hooks/useProductTranslation';
import { useDynamicTaxonomyTranslation } from '../hooks/useDynamicTaxonomyTranslation';
import { useReferralTracking } from '../hooks/useReferralTracking';
import { useCategoriesData } from '../hooks/useCategoriesData';
import { buildProductsPath, buildProductPath } from '../lib/seoUrls';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryImages = {
  'ebooks': 'https://images.unsplash.com/photo-1752442533957-5300db5b6d04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxyZWFkaW5nJTIwdGFibGV0JTIwZS1ib29rfGVufDB8fHx8MTc3NTQxODQxM3ww&ixlib=rb-4.1.0&q=85',
  'mobile-accessories': 'https://images.unsplash.com/photo-1760462788238-c9111e060692?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxzbWFydHBob25lJTIwYWNjZXNzb3JpZXMlMjBkZXNrfGVufDB8fHx8MTc3NTQxODQyNnww&ixlib=rb-4.1.0&q=85',
  'medical': 'https://images.pexels.com/photos/19080517/pexels-photo-19080517.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'cosmetics': 'https://images.unsplash.com/photo-1627811015433-368c148f6c3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwY29zbWV0aWNzJTIwc2tpbmNhcmV8ZW58MHx8fHwxNzc1NDE4NDEzfDA&ixlib=rb-4.1.0&q=85',
  'fashion': 'https://images.pexels.com/photos/15735256/pexels-photo-15735256.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'emergency': 'https://customer-assets.emergentagent.com/job_global-ebook-shop/artifacts/yzg69p9k_Sollar%20Cell%20Emergency%20Electricity%20Station.png',
  'smart-products': 'https://images.unsplash.com/photo-1558089687-f282ffcbc126?w=400',
  'food-products': 'https://images.unsplash.com/photo-1655740098787-7891a400c92b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHx0dXJraXNoJTIwY29mZmVlJTIwZGF0ZXMlMjBob25leSUyMGZvb2QlMjBwcm9kdWN0c3xlbnwwfHx8fDE3NzYyMzEzMjV8MA&ixlib=rb-4.1.0&q=85',
  'kitchen-smart-tools': 'https://images.unsplash.com/photo-1537084278693-d597e79f2939?w=400'
};

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  useReferralTracking();
  const [cartCount, setCartCount] = useState(0);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const { categories } = useCategoriesData();
  const [openCatId, setOpenCatId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchText, setSearchText] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [selectedRating, setSelectedRating] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const { formatPrice } = useCurrency();
  const { translatedProducts, translating: productTranslating } = useProductTranslation(products);
  useDynamicTaxonomyTranslation(categories);

  useEffect(() => {
    fetchCartCount();
    loadRecentlyViewed();
    fetchTrending();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTrending = async () => {
    try {
      // "Trending" = top 6 highest-rated products with reviews
      const res = await axios.get(`${API}/products?sort_by=rating&sort_order=desc&min_rating=4&limit=6`);
      setTrendingProducts(res.data.products || []);
    } catch (e) {
      console.debug('Failed to load trending products:', e?.message);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, searchText, minPrice, maxPrice, selectedRating]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (searchText) params.append('search', searchText);
      if (minPrice) params.append('min_price', minPrice);
      if (maxPrice) params.append('max_price', maxPrice);
      if (selectedRating > 0) params.append('min_rating', selectedRating);
      // Show all products on the homepage (no artificial cap)
      params.append('limit', '1000');
      const res = await axios.get(`${API}/products?${params}`);
      setProducts(res.data.products || []);
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCartCount = () => {
    const sessionId = localStorage.getItem('session_id') || generateSessionId();
    axios.get(`${API}/cart/${sessionId}`)
      .then(response => setCartCount(response.data.reduce((sum, item) => sum + item.cart_item.quantity, 0)))
      .catch((err) => console.debug('Cart count fetch failed:', err?.message || err));
  };

  const generateSessionId = () => {
    const id = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('session_id', id);
    return id;
  };

  const addToCart = async (productId) => {
    const sessionId = localStorage.getItem('session_id') || generateSessionId();
    try {
      await axios.post(`${API}/cart`, { session_id: sessionId, product_id: productId, quantity: 1 });
      toast.success('Added to cart');
      fetchCartCount();
    } catch (err) {
      console.error('Add to cart failed:', err);
      toast.error('Failed to add to cart');
    }
  };

  const loadRecentlyViewed = async () => {
    try {
      const stored = JSON.parse(localStorage.getItem('recently_viewed') || '[]');
      if (stored.length === 0) return;
      const items = [];
      for (const pid of stored.slice(0, 6)) {
        try {
          const res = await axios.get(`${API}/products/${pid}`);
          items.push(res.data);
        } catch (err) {
          console.debug(`Skipping recently-viewed product ${pid}:`, err?.message || err);
        }
      }
      setRecentlyViewed(items);
    } catch (err) {
      console.error('Failed to load recently viewed products:', err);
    }
  };

  const clearFilters = () => {
    setSearchText('');
    setSelectedRating(0);
    setMinPrice('');
    setMaxPrice('');
    setSelectedCategory('');
  };

  const activeFiltersCount = [searchText, selectedRating > 0, minPrice, maxPrice].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Header cartCount={cartCount} />

      {/* Promo Banner — directly below header */}
      <section className="bg-gradient-to-r from-[#1C241E] via-[#2A3530] to-[#1C241E] text-white" data-testid="home-promo-banner">
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="text-[#D4A017] text-xs uppercase tracking-[0.2em] mb-2 font-semibold">{t('banner.eyebrow', 'Limited Time')}</div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-light leading-tight mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('banner.title', 'Up to 30% off your first order at VEEVAMMERCE STORE')}
            </h2>
            <p className="text-white/80 text-sm md:text-base">
              {t('banner.subtitle', 'Free worldwide shipping on orders over $50. Use code WELCOME10 at checkout for an extra 10% off.')}
            </p>
          </div>
          <div className="flex md:justify-end">
            <Link to="/products" data-testid="banner-cta">
              <button className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#C9A227] text-[#1C241E] font-semibold px-6 py-3 rounded-full transition-transform hover:-translate-y-0.5">
                {t('banner.cta', 'Shop the Sale')} <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="hero-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <motion.div className="lg:col-span-5" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-light mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('hero.title')}
            </h1>
            <p className="text-lg text-[#5C6B61] leading-relaxed mb-8">{t('hero.subtitle')}</p>
            <Link to="/products" data-testid="hero-cta-button">
              <button className="bg-[#D96C4A] hover:bg-[#C05A3D] text-white px-8 py-3 rounded-full hover:-translate-y-0.5 flex items-center space-x-2 transition-transform">
                <span>{t('hero.cta')}</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </Link>
          </motion.div>
          <motion.div className="lg:col-span-7" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}>
            <div className="relative overflow-hidden rounded-xl">
              <img src="https://customer-assets.emergentagent.com/job_global-ebook-shop/artifacts/3qb7lmpb_images.jpg" alt="Hero" className="w-full h-auto object-cover image-hover-scale" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories Section — click any tile → reveals all its subcategories inline */}
      <section className="max-w-7xl mx-auto px-6 py-8" data-testid="categories-section">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-snug font-medium mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          {t('categories.title')}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-4">
          {categories.map((cat, index) => {
            const isOpen = openCatId === cat.id;
            const imgSrc = cat.image_url || categoryImages[cat.id];
            return (
              <motion.div key={cat.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }}>
                <button
                  type="button"
                  onClick={() => setOpenCatId(isOpen ? null : cat.id)}
                  className="block w-full text-left"
                  data-testid={`category-card-${cat.id}`}
                  aria-expanded={isOpen}
                >
                  <div className={`bg-white border rounded-xl overflow-hidden hover:-translate-y-1 transition-all group ${isOpen ? 'border-[#D96C4A] ring-2 ring-[#D96C4A]/30' : 'border-[rgba(28,36,30,0.12)]'}`}>
                    <div className="aspect-square bg-[#F0EFEB] relative overflow-hidden">
                      {imgSrc && (
                        <img src={imgSrc} alt={cat.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <span className="absolute bottom-1.5 right-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/90 text-[#D96C4A] text-[10px] font-semibold">
                        {(cat.subcategories || []).length}
                      </span>
                    </div>
                    <div className="p-3 text-center">
                      <h3 className="text-sm font-medium truncate" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                        {t(`cat.${cat.id}`, cat.name)}
                      </h3>
                    </div>
                  </div>
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Inline subcategory panel — appears below the grid when a category is clicked */}
        {openCatId && (() => {
          const openCat = categories.find((c) => c.id === openCatId);
          if (!openCat) return null;
          return (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="mt-6 bg-white border border-[#D96C4A]/40 rounded-2xl p-6 shadow-sm"
              data-testid={`subcat-panel-${openCat.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {t(`cat.${openCat.id}`, openCat.name)} <span className="text-sm text-[#5C6B61] font-normal">({(openCat.subcategories || []).length})</span>
                </h3>
                <div className="flex items-center gap-3">
                  <Link
                    to={buildProductsPath(i18n.language, openCat.id)}
                    className="text-sm text-[#D96C4A] hover:underline font-medium"
                    data-testid={`subcat-panel-viewall-${openCat.id}`}
                  >
                    {t('categories.viewAllInCat', 'View all →')}
                  </Link>
                  <button
                    onClick={() => setOpenCatId(null)}
                    className="text-[#5C6B61] hover:text-[#D96C4A] text-xs"
                    data-testid="subcat-panel-close"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {(openCat.subcategories || []).length === 0 ? (
                <p className="text-sm text-[#5C6B61]">{t('categories.noSubs', 'No subcategories yet.')}</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {openCat.subcategories.map((sub) => (
                    <Link
                      key={sub.id}
                      to={buildProductsPath(i18n.language, openCat.id, sub.id)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[rgba(28,36,30,0.08)] hover:border-[#D96C4A] hover:bg-[#FFF1EC] text-xs text-[#1C241E] transition-colors"
                      data-testid={`subcat-pill-${sub.id}`}
                    >
                      {sub.image_url && (
                        <img src={sub.image_url} alt="" loading="lazy" decoding="async" className="w-5 h-5 rounded object-cover flex-shrink-0" />
                      )}
                      <span className="truncate">
                        {t(`sub.${sub.id}`, sub.name || sub.id.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </motion.div>
          );
        })()}
      </section>

      {/* Deals & New Arrivals — quick links */}
      <section className="max-w-7xl mx-auto px-6 py-8" data-testid="home-deals-new-arrivals">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to={`${buildProductsPath(i18n.language)}?sort=deals`} data-testid="card-deals">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#D96C4A] to-[#C05A3D] text-white p-8 hover:shadow-lg transition-shadow group">
              <div className="text-xs uppercase tracking-[0.25em] text-white/80 mb-2 font-semibold">{t('home.deals.eyebrow', 'Hot Deals')}</div>
              <h3 className="text-3xl md:text-4xl font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('home.deals.title', 'Deals of the Day')}
              </h3>
              <p className="text-white/85 text-sm mb-4 max-w-md">{t('home.deals.body', 'Discounted picks across every category — refreshed every 24 hours.')}</p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all">
                {t('home.deals.cta', 'Shop Deals')} <ArrowRight className="w-4 h-4" />
              </span>
              <div className="absolute -right-6 -bottom-6 text-[110px] font-bold text-white/10 leading-none select-none">%</div>
            </div>
          </Link>

          <Link to={`${buildProductsPath(i18n.language)}?sort=new`} data-testid="card-new-arrivals">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1C241E] to-[#2A3530] text-white p-8 hover:shadow-lg transition-shadow group">
              <div className="text-xs uppercase tracking-[0.25em] text-[#D4A017] mb-2 font-semibold">{t('home.new.eyebrow', 'Just In')}</div>
              <h3 className="text-3xl md:text-4xl font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('home.new.title', 'New Arrivals')}
              </h3>
              <p className="text-white/85 text-sm mb-4 max-w-md">{t('home.new.body', 'Be the first to discover our latest products across home, travel, kids and more.')}</p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#D4A017] group-hover:gap-2 transition-all">
                {t('home.new.cta', 'Discover')} <ArrowRight className="w-4 h-4" />
              </span>
              <div className="absolute -right-4 -bottom-4 text-[110px] font-bold text-white/10 leading-none select-none">★</div>
            </div>
          </Link>
        </div>
      </section>

      {/* Trending Now — top 6 highest-rated products */}
      {trendingProducts.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-12" data-testid="home-trending-now">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#D96C4A] mb-1 font-semibold flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> {t('home.trending.eyebrow', 'Trending Now')}
              </div>
              <h2 className="text-3xl md:text-4xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('home.trending.title', 'What everyone is loving')}
              </h2>
            </div>
            <Link to={`${buildProductsPath(i18n.language)}?sort=trending`} className="text-sm text-[#D96C4A] hover:underline font-semibold flex items-center gap-1" data-testid="trending-view-all">
              {t('home.trending.viewAll', 'View all')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {trendingProducts.map((p, idx) => (
              <Link
                key={p.id}
                to={`/products/${p.id}`}
                className="group bg-white rounded-xl overflow-hidden border border-[rgba(28,36,30,0.08)] hover:shadow-lg transition-shadow relative"
                data-testid={`trending-product-${p.id}`}
              >
                <div className="absolute top-2 left-2 z-10 bg-[#1C241E] text-[#D4A017] text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5">
                  #{idx + 1}
                </div>
                <div className="aspect-square overflow-hidden bg-[#F0EFEB]">
                  <img
                    src={p.image_url}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    onError={(e) => { if (!e.target.dataset.f) { e.target.dataset.f = '1'; e.target.src = 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80'; } }}
                  />
                </div>
                <div className="p-3 space-y-1">
                  <h3 className="text-xs font-medium text-[#1C241E] line-clamp-2 group-hover:text-[#D96C4A] transition-colors">{p.name}</h3>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-bold text-[#3D6B44]">{formatPrice(p.price)}</div>
                    <div className="flex items-center gap-0.5 text-[10px] text-[#D4A017]">
                      <Star className="w-3 h-3 fill-current" /> {(p.rating || 0).toFixed(1)}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Search + Filter + Products Section */}
      <section className="max-w-7xl mx-auto px-6 py-8" data-testid="home-products-section">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl sm:text-3xl tracking-tight leading-snug font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {selectedCategory ? t(`cat.${selectedCategory}`, selectedCategory) : t('general.allProducts', 'All Products')}
          </h2>
          <Link to={`/products${selectedCategory ? `?category=${selectedCategory}` : ''}`} className="text-sm text-[#D96C4A] hover:text-[#C05A3D] flex items-center gap-1" data-testid="view-all-link">
            {t('general.viewAll', 'View All')} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Search + Category Pills */}
        <div className="bg-white border border-[rgba(28,36,30,0.08)] rounded-xl p-5 mb-8 shadow-sm">
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5C6B61]" />
            <input
              type="text" value={searchText} onChange={(e) => setSearchText(e.target.value)}
              placeholder={t('filters.searchPlaceholder', 'Search products by name, brand, or SKU...')}
              className="w-full pl-12 pr-4 py-3 bg-white border border-[rgba(28,36,30,0.12)] rounded-lg focus:outline-none focus:border-[#D96C4A]"
              data-testid="home-search-input"
            />
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap gap-2 mb-3">
            <button
              onClick={() => setSelectedCategory('')}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                !selectedCategory ? 'bg-[#D96C4A] text-white border-[#D96C4A]' : 'bg-white border-[rgba(28,36,30,0.12)] hover:border-[#D96C4A] text-[#1C241E]'
              }`}
              data-testid="home-category-all"
            >
              {t('general.all', 'All')}
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(selectedCategory === cat.id ? '' : cat.id)}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                  selectedCategory === cat.id ? 'bg-[#D96C4A] text-white border-[#D96C4A]' : 'bg-white border-[rgba(28,36,30,0.12)] hover:border-[#D96C4A] text-[#1C241E]'
                }`}
                data-testid={`home-category-${cat.id}`}
              >
                {t(`cat.${cat.id}`, cat.name)}
              </button>
            ))}
          </div>

          {/* Filters Toggle */}
          <div className="flex items-center justify-between">
            <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 text-[#1C241E] hover:text-[#D96C4A] text-sm" data-testid="home-toggle-filters">
              <SlidersHorizontal className="w-4 h-4" />
              <span>{t('filters.title', 'Filters')}</span>
              {activeFiltersCount > 0 && <span className="bg-[#D96C4A] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{activeFiltersCount}</span>}
            </button>
            {activeFiltersCount > 0 && (
              <button onClick={clearFilters} className="text-xs text-[#D96C4A] hover:text-[#C05A3D] flex items-center gap-1">
                <X className="w-3 h-3" /> {t('filters.clearAll', 'Clear')}
              </button>
            )}
          </div>

          {showFilters && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="border-t border-[rgba(28,36,30,0.08)] mt-3 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium mb-2 text-[#5C6B61]">Min Rating</label>
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5].map(r => (
                    <button key={r} onClick={() => setSelectedRating(r === selectedRating ? 0 : r)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs transition-colors ${selectedRating === r ? 'bg-[#D96C4A] text-white border-[#D96C4A]' : 'bg-white border-[rgba(28,36,30,0.12)] hover:border-[#D96C4A]'}`}
                      data-testid={`home-rating-${r}`}>
                      <Star className={`w-3 h-3 ${selectedRating === r ? 'fill-white' : 'fill-[#D96C4A]'}`} /> {r}+
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-2 text-[#5C6B61]">Price Range</label>
                <div className="flex items-center gap-2">
                  <input type="number" value={minPrice} onChange={e => setMinPrice(e.target.value)} placeholder="Min $"
                    className="flex-1 px-3 py-1.5 border border-[rgba(28,36,30,0.12)] rounded-lg text-sm focus:outline-none focus:border-[#D96C4A]" data-testid="home-min-price" />
                  <span className="text-[#5C6B61] text-xs">-</span>
                  <input type="number" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} placeholder="Max $"
                    className="flex-1 px-3 py-1.5 border border-[rgba(28,36,30,0.12)] rounded-lg text-sm focus:outline-none focus:border-[#D96C4A]" data-testid="home-max-price" />
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12"><div className="animate-pulse text-[#5C6B61]">Loading products...</div></div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[#5C6B61]">No products found</p>
            {(searchText || selectedCategory || activeFiltersCount > 0) && (
              <button onClick={clearFilters} className="mt-3 text-sm text-[#D96C4A] hover:text-[#C05A3D] underline">Clear filters</button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10">
            {translatedProducts.map((product, index) => (
              <motion.div key={product.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: index * 0.04 }}
                data-testid={`home-product-card-${product.id}`}>
                <Link to={buildProductPath(i18n.language, product.id)}>
                  <div className="aspect-[4/5] bg-[#F0EFEB] overflow-hidden rounded-xl mb-3">
                    <img src={product.image_url} alt={product.name} loading="lazy" decoding="async" className="w-full h-full object-cover image-hover-scale" />
                  </div>
                </Link>
                <div className="space-y-1.5">
                  {product.product_type === 'ebook' && (
                    <span className="inline-block rounded-full px-2.5 py-0.5 bg-[#F0EFEB] text-[#1C241E] text-xs tracking-wider uppercase">E-Book</span>
                  )}
                  <Link to={buildProductPath(i18n.language, product.id)}>
                    <h3 className="text-lg font-medium hover:text-[#D96C4A] truncate" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{product.name}</h3>
                  </Link>
                  {product.rating > 0 && (
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((starIndex) => <Star key={`star-${product.id}-${starIndex}`} className={`w-3.5 h-3.5 ${starIndex <= Math.floor(product.rating) ? 'fill-[#D96C4A] text-[#D96C4A]' : 'text-[#E5E4E0]'}`} />)}
                      <span className="text-xs text-[#5C6B61] ml-1">{product.rating.toFixed(1)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{formatPrice(product.price)}</span>
                    <button onClick={(e) => { e.preventDefault(); addToCart(product.id); }}
                      className="bg-[#D96C4A] hover:bg-[#C05A3D] text-white px-4 py-1.5 rounded-full text-xs transition-colors"
                      data-testid={`home-add-to-cart-${product.id}`}>
                      {t('product.addToCart')}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Recently Viewed */}
      {recentlyViewed.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-10" data-testid="recently-viewed-section">
          <h2 className="text-2xl sm:text-3xl tracking-tight leading-snug font-medium mb-6 flex items-center gap-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            <Clock className="w-6 h-6 text-[#D96C4A]" /> Recently Viewed
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {recentlyViewed.map((product, index) => (
              <motion.div key={product.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }}>
                <Link to={buildProductPath(i18n.language, product.id)} data-testid={`recently-viewed-${product.id}`}>
                  <div className="bg-white border border-[rgba(28,36,30,0.08)] rounded-xl overflow-hidden hover:-translate-y-0.5 transition-transform group">
                    <div className="aspect-square bg-[#F0EFEB] overflow-hidden">
                      <img src={product.image_url} alt={product.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-medium truncate" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{product.name}</h3>
                      <p className="text-sm text-[#D96C4A] mt-1">{formatPrice(product.price)}</p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default HomePage;
